import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Network, Plus, Pencil, Trash2, Star, Users, UserCog,
} from "lucide-react";
import { toast } from "sonner";
import type { ShipperNetwork } from "@/hooks/useShipperNetworks";
import { NetworkFormDialog } from "./NetworkFormDialog";
import ManageGroupBrokersSheet from "./ManageGroupBrokersSheet";
import { getBrokerGroupColor } from "@/lib/lrDirect/brokerGroupColors";

interface Props {
  networks: ShipperNetwork[];
  shipperUserId: string;
  shipperCompanyId: string | null;
  onCreate: (values: { name: string; description: string | null; is_default: boolean; color: string }) => Promise<void>;
  onUpdate: (id: string, values: { name: string; description: string | null; is_default: boolean; color: string }) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onSetDefault: (id: string) => Promise<void>;
  /** Bump to open the create dialog externally. */
  externalCreateSignal?: number;
  /** Called after any mutation so parent can refresh counts. */
  onMutated?: () => void;
}

export function BrokerGroupsSection({
  networks, shipperUserId, shipperCompanyId,
  onCreate, onUpdate, onDelete, onSetDefault,
  externalCreateSignal, onMutated,
}: Props) {
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ShipperNetwork | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ShipperNetwork | null>(null);
  const [deleteAck, setDeleteAck] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [manageTarget, setManageTarget] = useState<ShipperNetwork | null>(null);

  useEffect(() => {
    if (externalCreateSignal === undefined || externalCreateSignal <= 0) return;
    setEditing(null);
    setFormOpen(true);
  }, [externalCreateSignal]);

  const handleSetDefault = async (id: string) => {
    setBusyId(id);
    try { await onSetDefault(id); toast.success("Default group updated"); onMutated?.(); }
    catch (e: any) { toast.error(e?.message || "Failed"); }
    finally { setBusyId(null); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await onDelete(deleteTarget.id);
      toast.success("Group deleted — brokers moved to your default group");
      setDeleteTarget(null);
      setDeleteAck(false);
      onMutated?.();
    } catch (e: any) { toast.error(e?.message || "Failed to delete"); }
    finally { setBusyId(null); }
  };

  return (
    <>
      <Card className="overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between bg-card gap-2">
          <h2 className="text-sm font-bold uppercase tracking-wider flex items-center gap-2 min-w-0">
            <Network className="h-4 w-4 text-brand-teal" /> Broker Groups
            <Badge variant="secondary" className="text-[10px] ml-1">{networks.length}</Badge>
          </h2>
          <Button
            size="sm"
            onClick={() => { setEditing(null); setFormOpen(true); }}
            className="gap-1.5 text-white h-8"
            style={{ backgroundColor: "hsl(var(--brand-teal))" }}
          >
            <Plus className="h-3.5 w-3.5" /> Create Group
          </Button>
        </div>

        {networks.length === 0 ? (
          <div className="p-8 text-center">
            <h3 className="text-base font-bold text-foreground">
              Get started with your first broker group
            </h3>
            <p className="mt-1.5 text-sm text-muted-foreground max-w-md mx-auto">
              Groups let you organize trusted brokers by lane, region, or customer — and target private freight to just the right ones.
            </p>
            <Button
              className="mt-4 gap-1.5 text-white h-10 px-5"
              style={{ backgroundColor: "hsl(var(--brand-teal))" }}
              onClick={() => { setEditing(null); setFormOpen(true); }}
            >
              <Plus className="h-4 w-4" /> Create Your First Group
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {networks.map((n) => {
              const busy = busyId === n.id;
              const color = getBrokerGroupColor(n.color);
              const isLast = networks.length === 1;
              return (
                <li
                  key={n.id}
                  className="p-4 flex items-center justify-between gap-3 transition-colors"
                  style={{
                    borderLeft: `3px solid ${color.border}`,
                    backgroundColor: color.tintBg,
                  }}
                >
                  <div className="min-w-0 flex-1 flex items-center gap-2.5">
                    <span
                      className="h-3 w-3 rounded-full shrink-0 border border-black/10"
                      style={{ backgroundColor: color.hex }}
                      aria-hidden
                    />
                    {n.is_default ? (
                      <Star className="h-4 w-4 text-amber-500 shrink-0" fill="currentColor" />
                    ) : (
                      <span className="h-4 w-4 shrink-0" aria-hidden />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm text-foreground truncate">{n.name}</span>
                        {n.is_default && (
                          <Badge className="bg-brand-teal/15 text-brand-teal border-0 text-[10px] uppercase font-bold">
                            Default
                          </Badge>
                        )}
                        <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                          <Users className="h-3 w-3" /> {n.member_count ?? 0} broker{(n.member_count ?? 0) === 1 ? "" : "s"}
                        </span>
                      </div>
                      {n.description && (
                        <div className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">{n.description}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1.5 text-xs"
                      title="Add or remove brokers in this group"
                      onClick={() => setManageTarget(n)}
                    >
                      <UserCog className="h-3.5 w-3.5" /> Manage Brokers
                    </Button>
                    {!n.is_default && (
                      <Button
                        size="sm" variant="ghost" className="h-8 px-2 text-muted-foreground hover:text-amber-500"
                        title="Make default"
                        disabled={busy}
                        onClick={() => handleSetDefault(n.id)}
                      >
                        <Star className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    <Button
                      size="sm" variant="ghost" className="h-8 px-2"
                      title="Edit name / color / description"
                      onClick={() => { setEditing(n); setFormOpen(true); }}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm" variant="ghost"
                      className="h-8 px-2 text-destructive hover:text-destructive hover:bg-destructive/10 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={isLast ? "You need at least one group. Rename it instead." : "Delete group"}
                      disabled={busy || isLast}
                      onClick={() => {
                        if (isLast) {
                          toast.info("You need at least one group. Rename it instead of deleting.");
                          return;
                        }
                        setDeleteTarget(n);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <NetworkFormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        network={editing}
        hasAnyNetworks={networks.length > 0}
        onSubmit={async (values) => {
          if (editing) await onUpdate(editing.id, values);
          else await onCreate(values);
          onMutated?.();
        }}
      />

      {manageTarget && (
        <ManageGroupBrokersSheet
          open={!!manageTarget}
          onOpenChange={(v) => { if (!v) setManageTarget(null); }}
          targetNetworkId={manageTarget.id}
          targetNetworkName={manageTarget.name}
          allNetworks={networks.map((n) => ({ id: n.id, name: n.name }))}
          shipperUserId={shipperUserId}
          shipperCompanyId={shipperCompanyId}
          onMutated={() => { onMutated?.(); }}
        />
      )}

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) { setDeleteTarget(null); setDeleteAck(false); } }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.name}"?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>
                  <span className="font-semibold text-foreground">Your brokers stay in your network.</span>{" "}
                  Accepted brokers in this group will be moved to your default group
                  {(() => {
                    const dest = networks.find((n) => n.id !== deleteTarget?.id && n.is_default)
                      ?? networks.find((n) => n.id !== deleteTarget?.id);
                    return dest
                      ? <> (<span className="font-semibold text-foreground">{dest.name}</span>)</>
                      : <> (a new <span className="font-semibold text-foreground">"My Brokers"</span> group will be created)</>;
                  })()}.
                </p>
                <p>Pending invitations to this group will be cancelled.</p>
                <p>Loads previously posted to this group keep their history but stop being private to it.</p>
                <p className="text-muted-foreground">This cannot be undone.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <label className="flex items-start gap-2 rounded-md border border-border bg-muted/40 p-3 cursor-pointer">
            <Checkbox
              checked={deleteAck}
              onCheckedChange={(v) => setDeleteAck(v === true)}
              className="mt-0.5"
            />
            <span className="text-sm leading-snug">
              I understand pending invitations to this group will be cancelled.
            </span>
          </label>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!deleteAck || busyId === deleteTarget?.id}
              onClick={(e) => { e.preventDefault(); if (deleteAck) handleDelete(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50 disabled:pointer-events-none"
            >
              Delete group
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
